import { isEqual } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";

import { Button } from "@/components/Elements";
import DashboardContent from "@/components/shared/DashboardContent";
import DashboardContentWrapper from "@/components/shared/DashboardContentWrapper";
import DashboardHeader from "@/components/shared/DashboardHeader";
import { useMainStore } from "@/contexts/Store";
import { ROUTES } from "@/features/risk-assessment";
import { AssessmentUpsert } from "@/features/risk-assessment/types/assessment";
import { usePreventUnsavedChanges } from "@/hooks/usePreventUnsavedChanges";

import { AssessmentAPI } from "../../../api/legacy/risk-assessment/AssessmentApi";
import AssessmentTemplate from "../components/Assessment/AssessmentTemplate/AssessmentTemplate";

function EditAssessmentTemplatePage() {
  const mainStore = useMainStore();
  const { workspaceID } = mainStore.context;

  const history = useHistory();
  const { templateId } = useParams<{ templateId: string }>();
  const [isSaving, setIsSaving] = useState(false);
  const [assessmentTemplate, setAssessmentTemplate] =
    useState<Partial<AssessmentUpsert> | null>(null);
  const originalAssessmentTemplate = useRef<Partial<AssessmentUpsert>>(null);

  useEffect(() => {
    if (!workspaceID) {
      return;
    }

    AssessmentAPI.get(workspaceID, Number(templateId))
      .then((_assessmentTemplate) => {
        const assessmentUpsert: AssessmentUpsert = {
          name: _assessmentTemplate.name,
          id: _assessmentTemplate.id,
          is_template: true,
          risk_methodology_id: _assessmentTemplate.risk_methodology.id,
          questionnaire_ids: _assessmentTemplate.questionnaires.map(
            (q) => q.id,
          ),
        };
        // @ts-expect-error TS(2540) FIXME: Cannot assign to 'current' because it is a read-on... Remove this comment to see the full error message
        originalAssessmentTemplate.current = assessmentUpsert;
        setAssessmentTemplate(assessmentUpsert);
      })
      .catch((error) => {
        mainStore.toast.setErrorFromResponse(
          error,
          "There was an issue loading the Assessment Template. Please try again",
        );
        history.push(
          generatePath(ROUTES.TEMPLATE_PATH, {
            workspace_id: Number(workspaceID),
          }),
        );
      });
  }, [templateId, workspaceID]);

  const unblock = usePreventUnsavedChanges(
    [!isEqual(originalAssessmentTemplate.current, assessmentTemplate)],
    [assessmentTemplate, originalAssessmentTemplate.current],
  );

  const onSave = () => {
    // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
    if (!assessmentTemplate.name) {
      return mainStore.toast.setErrorText(
        "Assessment Template name is required",
      );
    }

    // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
    if (!assessmentTemplate.risk_methodology_id) {
      return mainStore.toast.setErrorText("Risk Methodology is required");
    }

    // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
    if (!assessmentTemplate.questionnaire_ids?.length) {
      return mainStore.toast.setErrorText(
        "At least one Questionnaire Template must be selected",
      );
    }

    setIsSaving(true);

    AssessmentAPI.edit(
      workspaceID!,
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      assessmentTemplate.id,
      {
        assessment: {
          is_template: true,
          // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
          name: assessmentTemplate.name,
          // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
          questionnaire_ids: assessmentTemplate.questionnaire_ids,
        },
      },
    )
      .then(() => {
        mainStore.toast.setText("Assessment Template saved!");
        unblock();
        history.push(
          generatePath(ROUTES.TEMPLATE_PATH, {
            workspace_id: Number(workspaceID),
          }),
        );
      })
      .catch((error) => {
        mainStore.toast.setErrorFromResponse(
          error,
          "There was an issue saving. Please try again",
        );
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <DashboardContent>
      <DashboardHeader
        title="Edit Assessment Template"
        onBackClick={() =>
          history.push(
            generatePath(ROUTES.TEMPLATE_PATH, {
              workspace_id: Number(workspaceID),
            }),
          )
        }
        RightActionBar={
          <Button label="Save Template" onClick={onSave} disabled={isSaving} />
        }
      />
      <DashboardContentWrapper>
        {/* temporary scroll solution wrapping in .table-wrap */}
        <div className="table-wrap">
          <AssessmentTemplate
            assessmentTemplate={assessmentTemplate}
            onChange={setAssessmentTemplate}
          />
        </div>
      </DashboardContentWrapper>
    </DashboardContent>
  );
}

export default observer(EditAssessmentTemplatePage);
